import {
  Marker as MarkerComponent,
  InfoWindow as MarkerInfoWindow,
  OverlayView,
  OverlayViewF,
} from '@react-google-maps/api'
import React from 'react'
import styled from 'styled-components'

import useVisible from '@shared/innmaxLib/hooks/useVisible'
import GoogleMap from '@shared/innmaxMap/map/GoogleMap'
import { DeleteWindow } from './Window'

import { DeviceType } from '@services/constants'
import { IStatisticalByDeviceType } from '@services/device'
import { toThousandSeparator } from '@shared/innmaxLib/utils/webHelper'
import { Col, Row } from 'antd'
import { pluck, sum } from 'ramda'
import { ISessionMarkerProps } from '../map.types'

export default function SessionMarker({
  tooltip = true,
  deletable = false,
  draggable = false,
  selected = false,
  size,
  session,
  deviceCountInfo = {
    deviceCount: 0,
    deviceUnavailableCount: 0,
    deviceAvailableCount: 0,
    deviceChargingCount: 0,
  },
  statistical,
  children,
  onLeftClick,
  onRightClick,
  onMouseOver,
  onMouseOut,
  onDrag,
  onDragEnd,
  onDelete,
  baseZIndex = 1,
  ...others
}: ISessionMarkerProps) {
  const markerRef = React.useRef<any>()

  const [zIndex, setZIndex] = React.useState(baseZIndex)
  const [mouseOver, setMouseOver] = React.useState(false)

  React.useEffect(() => {
    setZIndex(baseZIndex)
  }, [baseZIndex])

  const { isStreetMode } = GoogleMap.useGoogleMap()

  const [markerPosition, setMarkerPosition] = React.useState<
    google.maps.LatLng | google.maps.LatLngLiteral
  >()

  const tooltipWindow = useVisible()

  const deleteWindow = useVisible()

  const markerVisible = useVisible()

  const handleLeftClick = React.useCallback(
    (e: any) => {
      session && onLeftClick && onLeftClick(session)
    },
    [onLeftClick]
  ) //eslint-disable-line

  const handleRightClick = React.useCallback((e: any) => {
    if (deletable) {
      deleteWindow.toggle()
    }
  }, []) //eslint-disable-line

  const handleOnDrag = React.useCallback(
    (e: google.maps.MapMouseEvent) => {
      setMarkerPosition(e.latLng?.toJSON())
      onDrag && onDrag()
    },
    [onDrag]
  ) //eslint-disable-line

  const handleOnDragEnd = React.useCallback(
    (e: google.maps.MapMouseEvent) => {
      setMarkerPosition(e.latLng?.toJSON())
      onDragEnd &&
        onDragEnd({
          ...session,
          lat: e?.latLng?.lat() || 0,
          lon: e?.latLng?.lng() || 0,
        })
    },
    [onDragEnd]
  ) //eslint-disable-line

  const handleMouseOver = React.useCallback((e: any) => {
    tooltipWindow.open()
    onMouseOver && onMouseOver()
    setZIndex(_zIndex => _zIndex + 1)
    setMouseOver(true)
  }, []) //eslint-disable-line

  const handleMouseOut = React.useCallback((e: any) => {
    tooltipWindow.close()
    onMouseOut && onMouseOut()
    setZIndex(zIndex => zIndex - 1)
    setMouseOver(false)
  }, []) //eslint-disable-line

  const handleOnMarkerVisibleChanged = React.useCallback(() => {
    if (markerRef) {
      markerRef?.current?.marker?.visible
        ? markerVisible.open()
        : markerVisible.close()
    }
    tooltipWindow.close()
  }, [markerRef]) //eslint-disable-line

  React.useEffect(() => {
    setMarkerPosition({
      lat: session?.lat,
      lng: session?.lon,
    })
  }, [session])

  React.useEffect(() => {
    // 關閉刪除window
    document.addEventListener('click', deleteWindow.close)
    return () => document.removeEventListener('click', deleteWindow.close)
  }, []) //eslint-disable-line

  const markerIcon = React.useMemo(
    () => ({
      url:
        'data:image/svg+xml;charset=UTF-8,' +
        encodeURIComponent(`
      <svg width="160" height="160" xmlns="http://www.w3.org/2000/svg">
        <circle cx="80" cy="80" r="80" fill="transparent"/>
      </svg>
    `),
      size: new google.maps.Size(160, 160),
      anchor: new google.maps.Point(80, 80),
    }),
    []
  )

  const sumConnectorDetail = (key: keyof IStatisticalByDeviceType) =>
    toThousandSeparator(
      sum(
        pluck(
          key as any,
          statistical?.filter(
            item => item.deviceType === DeviceType.CONNECTOR
          ) || []
        )
      )
    )

  if (!markerPosition?.lat || !markerPosition?.lng) {
    return <></>
  }
  return (
    <MarkerComponent
      ref={markerRef}
      title={isStreetMode ? session?.name || '' : ''}
      position={markerPosition as google.maps.LatLng}
      icon={markerIcon}
      onVisibleChanged={handleOnMarkerVisibleChanged}
      onDrag={handleOnDrag}
      onDragEnd={handleOnDragEnd}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
      onClick={handleLeftClick}
      onRightClick={handleRightClick}
      draggable={draggable}>
      <OverlayViewF
        mapPaneName={OverlayView.MARKER_LAYER}
        position={markerPosition as google.maps.LatLng}
        zIndex={zIndex}
        {...others}>
        <>
          {tooltip && tooltipWindow.visible && session?.name && (
            <MarkerInfoWindow
              position={markerPosition}
              options={{
                ...infoboxOptions,
                pixelOffset: new google.maps.Size(200, 100),
              }}>
              {draggable ? (
                <StyledTooltipWrapper>
                  <StyledTootipContent>{session?.name}</StyledTootipContent>
                  <StyledTooltipArrow></StyledTooltipArrow>
                </StyledTooltipWrapper>
              ) : (
                <DetailPanel>
                  <Row className="content">
                    <Col span={12} className="text-avaliable">
                      可使用
                    </Col>
                    <Col span={12} className="text-white">
                      {sumConnectorDetail('availableCount')}
                    </Col>
                    <Col span={12} className="text-reserved">
                      預定中
                    </Col>
                    <Col span={12} className="text-white">
                      {sumConnectorDetail('reservedCount')}
                    </Col>
                    <Col span={12} className="text-charging">
                      準備充電
                    </Col>
                    <Col span={12} className="text-white">
                      {sumConnectorDetail('preparingCount')}
                    </Col>
                    <Col span={12} className="text-charging">
                      充電中
                    </Col>
                    <Col span={12} className="text-white">
                      {sumConnectorDetail('chargingCount')}
                    </Col>
                    <Col span={12} className="text-charging">
                      充電完畢
                    </Col>
                    <Col span={12} className="text-white">
                      {sumConnectorDetail('finishingCount')}
                    </Col>
                    <Col span={12} className="text-unavaliable">
                      充電樁停止
                    </Col>
                    <Col span={12} className="text-white">
                      {sumConnectorDetail('suspendedEVSECount')}
                    </Col>
                    <Col span={12} className="text-unavaliable">
                      車輛停止
                    </Col>
                    <Col span={12} className="text-white">
                      {sumConnectorDetail('suspendedEVCount')}
                    </Col>
                    <Col span={12} className="text-[#859FAA]">
                      無服務
                    </Col>
                    <Col span={12} className="text-white">
                      {sumConnectorDetail('unavailableCount')}
                    </Col>
                    <Col span={12} className="text-unavaliable">
                      故障
                    </Col>
                    <Col span={12} className="text-white">
                      {sumConnectorDetail('faultedCount')}
                    </Col>
                  </Row>
                </DetailPanel>
              )}
            </MarkerInfoWindow>
          )}
          {deleteWindow.visible && (
            <MarkerInfoWindow
              position={markerPosition}
              options={{
                ...infoboxOptions,
                pixelOffset:
                  size === 'small'
                    ? new google.maps.Size(140, 30)
                    : new google.maps.Size(170, 30),
              }}>
              <DeleteWindow
                data={session}
                onCancel={deleteWindow.toggle}
                deleteMarker={() => onDelete && onDelete(session)}
              />
            </MarkerInfoWindow>
          )}
          <StyledMarkerWrapper hover={mouseOver}>
            <div className="text-xl font-semibold truncate w-120 text-center">
              {session.name}
            </div>
            <div className="text-lg font-semibold text-white">
              {toThousandSeparator(deviceCountInfo.deviceCount)}
            </div>
            <div className="font-medium text-lg">
              <span className="text-white">
                {toThousandSeparator(
                  deviceCountInfo.deviceAvailableCount +
                    deviceCountInfo.deviceChargingCount
                )}{' '}
                |{' '}
              </span>
              <span>
                {toThousandSeparator(deviceCountInfo.deviceUnavailableCount)}
              </span>
            </div>
          </StyledMarkerWrapper>
        </>
      </OverlayViewF>
    </MarkerComponent>
  )
}

const StyledMarkerWrapper = styled(({ ...props }) => <div {...props} />)`
  width: 130px;
  height: 130px;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgb(255, 204, 43, 0.8);
  margin-left: -65px;
  margin-top: -65px;

  position: relative;

  &::before {
    content: '';
    position: absolute;
    width: 180px;
    height: 180px;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      rgba(255, 204, 43, 0.1) 50%,
      rgba(255, 204, 43, 0.3) 60%,
      transparent 100%
    );
    filter: blur(5px);
    z-index: -2;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &::after {
    content: '';
    position: absolute;
    width: 160px;
    height: 160px;
    border-radius: 50%;
    border: 3px solid
      ${p => (p.hover ? 'rgba(255, 204, 43, 0.8)' : 'rgba(255, 204, 43, 0.3)')};
    box-shadow: 0 0 3px 3px rgba(255, 204, 43, 0.3);
    z-index: -1;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`

const StyledTooltipWrapper = styled.div`
  position: relative;
`

const StyledTootipContent = styled.div`
  background-color: ${p => p.theme.yellow100};
  border-radius: 2px;
  font-size: 13px;
  font-weight: normal;
  padding: 3px 8px;
  min-width: 68px;
  min-height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.2);
`

const StyledTooltipArrow = styled.div`
  background-color: transparent;
  margin: 0 auto;
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 10px solid ${p => p.theme.yellow100};
`

const infoboxOptions = {
  visible: true,
  alignBottom: false,
  disableAutoPan: false,
  enableEventPropagation: false,
  boxClass: 'style-info-box',
  boxStyle: {
    overflow: 'hidden',
  },
}

const DetailPanel = styled.div`
  position: relative;
  z-index: 999;
  width: 180px;
  background: rgba(92, 99, 101, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  display: block;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);

  p {
    margin: 0;
  }
  > .summary {
    background-color: rgba(255, 255, 255, 0.1);
  }
  .desc {
    color: rgba(255, 255, 255, 0.5);
  }
  .content,
  .summary {
    padding: 10px 15px;
    .ant-col:nth-child(2n) {
      text-align: right;
    }
  }
`
